import React, { useState, useEffect } from 'react';
import {
  Computer,
  Shield,
  AlertTriangle,
  Plus,
  Search,
  MoreVertical,
  Server,
  Wifi,
  HardDrive,
  Key,
  Building2,
  CheckCircle,
  Clock,
  AlertCircle,
  User,
  Mail,
  Phone,
  Globe,
  Eye,
  EyeOff,
  Copy,
  X,
  Download,
  Monitor,
  Printer,
  ShieldCheck,
  MapPin,
  Hash,
  Cpu,
  ChevronRight,
  Check,
  Loader2
} from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { apiService } from '../services/apiService';
import SupportToolsTab from '../components/ITAssets/SupportToolsTab';
import { authService } from '../services/authService';

interface LifecycleWarning {
  severity: 'info' | 'warning' | 'critical' | 'neutral';
  label: string;
  message: string;
  notes?: string;
  eos_date?: string;
  eol_date?: string;
}

interface Asset {
  pk: number;
  id?: string;
  name: string;
  hostname?: string;
  brand?: string;
  model?: string;
  asset_type?: string;
  device_status?: string;
  year?: number;
  variant?: string;
  serial_number?: string;
  service_tag?: string;
  status: string;
  type?: string;
  vendor?: string;
  location?: string;
  assigned_to?: string;
  purchase_date?: string;
  warranty_start?: string;
  warranty_end?: string;
  warranty_expires?: string;
  notes?: string;
  last_updated?: string;
  lifecycle_status?: string;
  eos_date?: string;
  eol_date?: string;
  lifecycle_notes?: string;
  lifecycle_warning?: LifecycleWarning | null;
}

interface Vendor {
  id: string;
  pk?: number;
  // Real field names from the CRM model
  vendor_name?: string;
  vendor_code?: string;
  vendor_account_number?: string;
  vendor_type?: string;
  vendor_status?: string;
  vendor_rating?: number;
  primary_contact_name?: string;
  account_name?: string;
  contact_number?: string;
  contact_email?: string;
  contact_website?: string;
  business_address?: string;
  description?: string;
  network_location_id?: number;
  network_location_name?: string;
  network_location_address?: string;
  // Legacy shims still returned by API
  name?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  website?: string;
  assets_count?: number;
}

interface Credential {
  id: string;
  service_name: string;
  username: string;
  // The list response carries NO secret. `can_reveal` says whether the audited
  // reveal endpoint will hand one over; the value itself is fetched on demand.
  can_reveal?: boolean;
  website?: string;
  category: 'system' | 'application' | 'network' | 'cloud';
  last_updated: string;
  expires?: string;
}

const ITAssets: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'vendors' | 'credentials' | 'tools'>('overview');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());
  // Secrets fetched via the audited reveal endpoint, cached per credential id
  // so toggling visibility twice doesn't raise a second Break Glass alert.
  const [revealedPasswords, setRevealedPasswords] = useState<Record<string, string>>({});
  const [revealingId, setRevealingId] = useState<string | null>(null);
  // Drives the transient "Copied!" affordance; cleared on a timer.
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedAssetDetail, setSelectedAssetDetail] = useState<Asset | null>(null);
  const [assetModalTab, setAssetModalTab] = useState<'details' | 'credentials'>('details');
  const [assetCredentials, setAssetCredentials] = useState<Credential[]>([]);
  const [assetCredentialsLoading, setAssetCredentialsLoading] = useState(false);
  const [showAddCredentialModal, setShowAddCredentialModal] = useState(false);
  const [newCredential, setNewCredential] = useState({
    title: '',
    username: '',
    password: '',
    url: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [locationView, setLocationView] = useState<'flat' | 'grouped' | 'sidebar'>('flat');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [vendorLocationView, setVendorLocationView] = useState<'flat' | 'sidebar'>('flat');
  const [selectedVendorLocation, setSelectedVendorLocation] = useState<string | null>(null);
  const [selectedVendorDetail, setSelectedVendorDetail] = useState<Vendor | null>(null);

  // Get customer info from auth service
  const userInfo = authService.getCurrentUser();
  const customerId = userInfo?.account_id?.toString() || 'default';
  const customerName = userInfo?.account?.name || 'Your Organization';

  useEffect(() => {
    loadITData();
  }, []);

  // Fetch (and cache) the real secret. The list endpoint intentionally returns
  // none, so this is the only place a password enters the browser — and the
  // only thing that writes the audit/Break Glass trail server-side.
  const ensurePasswordRevealed = async (credentialId: string): Promise<string | null> => {
    if (revealedPasswords[credentialId]) {
      return revealedPasswords[credentialId];
    }
    setRevealingId(credentialId);
    try {
      const response = await apiService.revealCredential(credentialId);
      const password = response?.result?.password;
      if (response?.errors || !password) {
        return null;
      }
      setRevealedPasswords(prev => ({ ...prev, [credentialId]: password }));
      return password;
    } catch (error) {
      console.error('Failed to reveal credential:', error);
      return null;
    } finally {
      setRevealingId(null);
    }
  };

  const togglePasswordVisibility = async (credentialId: string) => {
    if (visiblePasswords.has(credentialId)) {
      setVisiblePasswords(prev => {
        const newSet = new Set(prev);
        newSet.delete(credentialId);
        return newSet;
      });
      return;
    }
    const password = await ensurePasswordRevealed(credentialId);
    if (password) {
      setVisiblePasswords(prev => new Set(prev).add(credentialId));
    }
  };

  // Flash "Copied!" for a moment. Keyed so two rows can't both look copied.
  const flagCopied = (key: string) => {
    setCopiedId(key);
    setTimeout(() => setCopiedId(prev => (prev === key ? null : prev)), 2000);
  };

  const copyText = async (text: string, key: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    flagCopied(key);
  };

  const copyPassword = async (credentialId: string) => {
    const password = await ensurePasswordRevealed(credentialId);
    if (password) {
      await navigator.clipboard.writeText(password);
      flagCopied(`pw-${credentialId}`);
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'system':   return 'bg-blue-100 text-blue-700';
      case 'cloud':    return 'bg-indigo-100 text-indigo-700';
      case 'network':  return 'bg-cyan-100 text-cyan-700';
      case 'application': return 'bg-violet-100 text-violet-700';
      default:         return 'bg-gray-100 text-gray-700';
    }
  };

  // Password cell shared by the Credentials tab and the asset modal, so both
  // get the same reveal spinner and copy confirmation.
  const renderSecretCell = (credential: Credential) => {
    if (!credential.can_reveal) {
      return (
        <div className="flex items-center space-x-1 text-gray-400">
          <EyeOff className="h-3.5 w-3.5" />
          <span className="text-xs italic">Hidden</span>
        </div>
      );
    }
    const isVisible = visiblePasswords.has(credential.id);
    const isRevealing = revealingId === credential.id;
    const isCopied = copiedId === `pw-${credential.id}`;
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm font-mono text-gray-900 min-w-[6rem]">
          {isRevealing ? (
            <span className="inline-flex items-center text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              <span className="text-xs">Revealing…</span>
            </span>
          ) : isVisible ? (
            revealedPasswords[credential.id] || '••••••••'
          ) : (
            '••••••••'
          )}
        </span>
        <button
          onClick={() => togglePasswordVisibility(credential.id)}
          disabled={isRevealing}
          className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors disabled:opacity-40"
          title={isVisible ? 'Hide' : 'Show'}
        >
          {isVisible ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
        <button
          onClick={() => copyPassword(credential.id)}
          disabled={isRevealing}
          className={`p-1 rounded transition-colors disabled:opacity-40 ${
            isCopied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'
          }`}
          title="Copy password"
        >
          {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </button>
        {isCopied && <span className="text-xs font-medium text-green-600">Copied!</span>}
      </div>
    );
  };

  const renderUsernameCell = (credential: Credential) => {
    const isCopied = copiedId === `user-${credential.id}`;
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm font-mono text-gray-900">{credential.username}</span>
        <button
          onClick={() => copyText(credential.username, `user-${credential.id}`)}
          className={`p-1 rounded transition-colors ${
            isCopied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'
          }`}
          title="Copy username"
        >
          {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </button>
      </div>
    );
  };

  // Open the asset modal and pull the credentials linked to that asset.
  const openAssetDetail = async (asset: Asset) => {
    setSelectedAssetDetail(asset);
    setAssetModalTab('details');
    setAssetCredentials([]);
    const assetId = asset.pk ?? asset.id;
    if (assetId === undefined || assetId === null) return;
    setAssetCredentialsLoading(true);
    try {
      const response = await apiService.getAssetCredentials(assetId);
      setAssetCredentials(response?.errors ? [] : (response?.result || []));
    } catch (error) {
      console.error('Failed to load asset credentials:', error);
      setAssetCredentials([]);
    } finally {
      setAssetCredentialsLoading(false);
    }
  };

  const handleAddCredential = async () => {
    setIsSubmitting(true);
    try {
      const response = await apiService.createCredential(newCredential);
      if (!response.errors) {
        // Add the new credential to the list
        setCredentials(prev => [...prev, response.result]);
        // Reset form and close modal
        setNewCredential({
          title: '',
          username: '',
          password: '',
          url: '',
          notes: ''
        });
        setShowAddCredentialModal(false);
        // You might want to add a toast notification here
      }
    } catch (error) {
      console.error('Failed to create credential:', error);
      // You might want to show an error toast here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddCredentialModal(false);
    setNewCredential({
      title: '',
      username: '',
      password: '',
      url: '',
      notes: ''
    });
  };

  const loadITData = async () => {
    setLoading(true);
    try {
      // Load real data from API endpoints
      const [assetsResponse, vendorsResponse, credentialsResponse] = await Promise.all([
        apiService.getAssets(),
        apiService.getVendors(),
        apiService.getCredentials()
      ]);

      if (!assetsResponse.errors) {
        // Portal API may return result directly or as model_list
        const assetList = assetsResponse.result?.model_list || assetsResponse.result || [];
        setAssets(Array.isArray(assetList) ? assetList : []);
      }
      
      if (!vendorsResponse.errors) {
        setVendors(vendorsResponse.result || []);
      }
      
      if (!credentialsResponse.errors) {
        setCredentials(credentialsResponse.result || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to load IT data:', error);
      setError('Failed to load IT asset data');
      setLoading(false);
      
      // Fallback to mock data for development (optional)
      /* 
      setTimeout(() => {
        setAssets([
          {
            id: '1',
            name: 'CEO-Desktop-01',
            type: 'desktop',
            vendor: 'Dell',
            model: 'OptiPlex 7090',
            serial_number: 'DL7090-2024-001',
            status: 'active',
            location: 'Office - CEO',
            assigned_to: 'John Smith',
            purchase_date: '2024-01-15',
            warranty_expires: '2027-01-15',
            last_updated: '2024-12-01'
          },
          {
            id: '2',
            name: 'DEV-Laptop-03',
            type: 'laptop',
            vendor: 'HP',
            model: 'ProBook 450 G9',
            serial_number: 'HP450G9-2024-003',
            status: 'active',
            location: 'Remote - Developer',
            assigned_to: 'Jane Doe',
            purchase_date: '2024-03-10',
            warranty_expires: '2027-03-10',
            last_updated: '2024-11-28'
          },
          {
            id: '3',
            name: 'Main-Server-01',
            type: 'server',
            vendor: 'HPE',
            model: 'ProLiant DL380 Gen10',
            serial_number: 'HPE380-2023-001',
            status: 'maintenance',
            location: 'Datacenter Rack A1',
            purchase_date: '2023-06-20',
            warranty_expires: '2026-06-20',
            last_updated: '2024-12-03'
          }
        ]);

        setVendors([
          {
            id: '1',
            name: 'Dell Technologies',
            contact_person: 'Mike Johnson',
            email: 'mike.johnson@dell.com',
            phone: '+1-555-0123',
            website: 'https://dell.com',
            assets_count: 5
          },
          {
            id: '2',
            name: 'HP Inc.',
            contact_person: 'Sarah Wilson',
            email: 'sarah.wilson@hp.com',
            phone: '+1-555-0124',
            website: 'https://hp.com',
            assets_count: 3
          }
        ]);

        setCredentials([
          {
            id: '1',
            service_name: 'Domain Administrator',
            username: 'admin@company.local',
            category: 'system',
            last_updated: '2024-11-15',
            expires: '2025-02-15'
          },
          {
            id: '2',
            service_name: 'Office 365 Admin',
            username: 'admin@company.com',
            website: 'https://admin.microsoft.com',
            category: 'cloud',
            last_updated: '2024-10-20'
          }
        ]);

        setLoading(false);
      }, 1000);
      */
    }
  };

  const getAssetIcon = (asset: Asset) => {
    const assetType = asset.asset_type || asset.type || '';
    switch (assetType.toLowerCase()) {
      case 'desktop':
      case 'workstation':
        return <Monitor className="h-5 w-5" />;
      case 'laptop':
        return <Computer className="h-5 w-5" />;
      case 'server':
        return <Server className="h-5 w-5" />;
      case 'router':
      case 'switch':
      case 'network':
      case 'access-point':
      case 'ap':
        return <Wifi className="h-5 w-5" />;
      case 'firewall':
        return <ShieldCheck className="h-5 w-5" />;
      case 'printer':
      case 'scanner':
        return <Printer className="h-5 w-5" />;
      case 'voip-phone':
      case 'phone':
      case 'mobile':
        return <Phone className="h-5 w-5" />;
      case 'ups':
      case 'pdu':
        return <Cpu className="h-5 w-5" />;
      default:
        return <HardDrive className="h-5 w-5" />;
    }
  };

  const getAssetTypeLabel = (asset: Asset) => {
    const assetType = asset.asset_type || asset.type || 'other';
    return assetType.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getAssetTypeColor = (asset: Asset) => {
    const assetType = asset.asset_type || asset.type || '';
    switch (assetType.toLowerCase()) {
      case 'server':
        return 'bg-blue-100 text-blue-700';
      case 'desktop':
      case 'workstation':
        return 'bg-indigo-100 text-indigo-700';
      case 'laptop':
        return 'bg-violet-100 text-violet-700';
      case 'router':
      case 'switch':
      case 'network':
      case 'access-point':
      case 'ap':
        return 'bg-cyan-100 text-cyan-700';
      case 'firewall':
        return 'bg-red-100 text-red-700';
      case 'printer':
      case 'scanner':
        return 'bg-amber-100 text-amber-700';
      case 'voip-phone':
      case 'phone':
        return 'bg-teal-100 text-teal-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'online':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'maintenance':
      case 'warning':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'inactive':
      case 'offline':
      case 'retired':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'online':
        return 'bg-green-100 text-green-800';
      case 'maintenance':
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
      case 'offline':
      case 'retired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return (status || 'unknown').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getLifecycleWarning = (asset: Asset): LifecycleWarning | null => {
    // If the backend already computed it, use it
    if (asset.lifecycle_warning) return asset.lifecycle_warning;
    // Otherwise derive from lifecycle_status
    const status = asset.lifecycle_status;
    if (!status || status === 'active') return null;
    const map: Record<string, LifecycleWarning> = {
      end_of_sale: {
        severity: 'info',
        label: 'End of Sale',
        message: 'This product is no longer sold by the manufacturer. Consider planning a replacement.',
      },
      end_of_support: {
        severity: 'warning',
        label: 'End of Support',
        message: 'This product no longer receives security patches or updates. It may pose a security risk.',
      },
      end_of_life: {
        severity: 'critical',
        label: 'End of Life',
        message: 'This product is End of Life and no longer supported. It is a security vulnerability and should be replaced.',
      },
      retired: {
        severity: 'neutral',
        label: 'Retired',
        message: 'This asset has been decommissioned.',
      },
    };
    const warning = map[status];
    if (!warning) return null;
    if (asset.lifecycle_notes) warning.notes = asset.lifecycle_notes;
    if (asset.eos_date) warning.eos_date = asset.eos_date;
    if (asset.eol_date) warning.eol_date = asset.eol_date;
    return warning;
  };

  const getLifecycleBadgeClasses = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'info': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'neutral': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getLifecycleBannerClasses = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-50 border-red-200 text-red-800';
      case 'warning': return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'info': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'neutral': return 'bg-gray-50 border-gray-200 text-gray-600';
      default: return 'bg-gray-50 border-gray-200 text-gray-600';
    }
  };

  const getLifecycleIconClasses = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600';
      case 'warning': return 'text-orange-500';
      case 'info': return 'text-yellow-600';
      default: return 'text-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const exportToCSV = (rows: Record<string, any>[], filename: string) => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csvRows = [
      headers.join(','),
      ...rows.map(row =>
        headers.map(h => {
          const val = row[h] == null ? '' : String(row[h]);
          return `"${val.replace(/"/g, '""')}"`;
        }).join(',')
      )
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading IT Assets</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  loadITData();
                }}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-bold text-gray-900">IT Asset Manager</h1>
            <p className="mt-2 text-gray-600">Manage your IT infrastructure, vendors, and credentials</p>
          </div>
          {activeTab === 'credentials' && (
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowAddCredentialModal(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span>Add Credential</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: Computer },
            { id: 'assets', name: 'Assets', icon: HardDrive },
            { id: 'vendors', name: 'Vendors', icon: Building2 },
            { id: 'credentials', name: 'Credentials', icon: Key },
            { id: 'tools', name: 'Support Tools', icon: Download }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-900">{assets.length}</p>
                  <p className="text-blue-700 font-medium">Total Assets</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Computer className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-900">
                    {assets.filter(a => ['active', 'online'].includes(a.status || a.device_status || '')).length}
                  </p>
                  <p className="text-green-700 font-medium">Online / Active</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-orange-900">{vendors.length}</p>
                  <p className="text-orange-700 font-medium">Vendors</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-900">{credentials.length}</p>
                  <p className="text-purple-700 font-medium">Credentials</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Key className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Lifecycle Warning Banner */}
          {(() => {
            const eolAssets = assets.filter(a => {
              const lc = a.lifecycle_status || '';
              return lc === 'end_of_life' || lc === 'end_of_support';
            });
            if (eolAssets.length === 0) return null;
            return (
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-red-900">
                      {eolAssets.length} {eolAssets.length === 1 ? 'asset requires' : 'assets require'} attention
                    </h3>
                    <p className="text-sm text-red-700 mt-1">
                      The following assets are End of Life or End of Support and may pose security risks. Contact your IT provider for replacement options.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {eolAssets.slice(0, 5).map((asset) => (
                        <span key={asset.pk || asset.id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {asset.name || asset.hostname || 'Unknown'} — {(asset.lifecycle_status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      ))}
                      {eolAssets.length > 5 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-200 text-red-900">
                          +{eolAssets.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Asset Type Breakdown */}
          {assets.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Asset Breakdown by Type</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(
                  assets.reduce((acc: Record<string, number>, a) => {
                    const t = a.asset_type || a.type || 'other';
                    acc[t] = (acc[t] || 0) + 1;
                    return acc;
                  }, {})
                ).sort((a, b) => b[1] - a[1]).map(([assetType, count]) => (
                  <div key={assetType} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className={`p-2 rounded-lg ${getAssetTypeColor({ asset_type: assetType } as Asset)}`}>
                      {getAssetIcon({ asset_type: assetType } as Asset)}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{count as number}</p>
                      <p className="text-xs text-gray-500 capitalize">{(assetType as string).replace(/-/g, ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Assets */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Recent Assets</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Asset
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lifecycle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Serial / Service Tag
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assets.slice(0, 5).map((asset) => {
                    const warning = getLifecycleWarning(asset);
                    return (
                    <tr key={asset.pk || asset.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`flex-shrink-0 p-2 rounded-lg ${getAssetTypeColor(asset)}`}>
                            {getAssetIcon(asset)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900">{asset.name || asset.hostname || 'Unnamed'}</div>
                            <div className="text-xs text-gray-500">
                              {[asset.brand || asset.vendor, asset.model].filter(Boolean).join(' ')}
                              {asset.year ? ` (${asset.year})` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAssetTypeColor(asset)}`}>
                          {getAssetTypeLabel(asset)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(asset.status || asset.device_status || 'unknown')}`}>
                          {getStatusIcon(asset.status || asset.device_status || 'unknown')}
                          <span className="ml-1">{getStatusLabel(asset.status || asset.device_status || 'unknown')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {warning ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getLifecycleBadgeClasses(warning.severity)}`}>
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {warning.label}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900 font-mono">{asset.serial_number || 'N/A'}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {asset.location || 'Not specified'}
                      </td>
                    </tr>
                    );
                  })}
                  {assets.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                        No assets found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Assets Tab */}
      {activeTab === 'assets' && (() => {
        // Filter assets based on search, status, and type
        const filteredAssets = assets.filter((asset) => {
          if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const searchable = [
              asset.name, asset.hostname, asset.brand, asset.vendor, asset.model,
              asset.serial_number, asset.service_tag, asset.asset_type, asset.type,
              asset.location, asset.assigned_to, asset.variant, asset.notes
            ].filter(Boolean).join(' ').toLowerCase();
            if (!searchable.includes(term)) return false;
          }
          if (statusFilter !== 'all') {
            const assetStatus = asset.status || asset.device_status || 'unknown';
            if (assetStatus !== statusFilter) return false;
          }
          if (typeFilter !== 'all') {
            const assetType = asset.asset_type || asset.type || 'other';
            if (assetType !== typeFilter) return false;
          }
          return true;
        });

        const assetTypes = Array.from(new Set(assets.map(a => a.asset_type || a.type || 'other'))).sort();
        const assetStatuses = Array.from(new Set(assets.map(a => a.status || a.device_status || 'unknown'))).sort();

        // Unique locations for sidebar/grouping
        const allLocations = Array.from(new Set(assets.map(a => a.location || 'Unassigned').filter(Boolean))).sort();

        // Apply location filter when in sidebar mode
        const displayedAssets = locationView === 'sidebar' && selectedLocation
          ? filteredAssets.filter(a => (a.location || 'Unassigned') === selectedLocation)
          : filteredAssets;

        // Group by location for grouped mode
        const grouped = allLocations
          .map(loc => ({
            location: loc,
            assets: displayedAssets.filter(a => (a.location || 'Unassigned') === loc)
          }))
          .filter(g => g.assets.length > 0);

        return (
          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, brand, model, serial, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
              >
                <option value="all">All Statuses</option>
                {assetStatuses.map(s => (
                  <option key={s} value={s}>{getStatusLabel(s)}</option>
                ))}
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
              >
                <option value="all">All Types</option>
                {assetTypes.map(t => (
                  <option key={t} value={t}>{t.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
              {/* View mode toggle */}
              <div className="flex items-center rounded-xl border border-gray-300 overflow-hidden bg-white text-sm flex-shrink-0">
                <button
                  onClick={() => setLocationView('flat')}
                  className={`px-3 py-2.5 flex items-center space-x-1.5 transition-colors ${locationView === 'flat' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  title="Flat list"
                >
                  <MapPin className="h-4 w-4" />
                  <span className="hidden sm:inline">Flat</span>
                </button>
                <button
                  onClick={() => setLocationView('grouped')}
                  className={`px-3 py-2.5 flex items-center space-x-1.5 border-l border-gray-300 transition-colors ${locationView === 'grouped' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  title="Group by location"
                >
                  <Building2 className="h-4 w-4" />
                  <span className="hidden sm:inline">By Site</span>
                </button>
                <button
                  onClick={() => { setLocationView('sidebar'); if (!selectedLocation && allLocations.length > 0) setSelectedLocation(allLocations[0]); }}
                  className={`px-3 py-2.5 flex items-center space-x-1.5 border-l border-gray-300 transition-colors ${locationView === 'sidebar' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  title="Site sidebar"
                >
                  <Hash className="h-4 w-4" />
                  <span className="hidden sm:inline">Sidebar</span>
                </button>
              </div>
              {/* Export */}
              <button
                onClick={() => exportToCSV(
                  displayedAssets.map(a => ({
                    Name: a.name || a.hostname || '',
                    Type: a.asset_type || a.type || '',
                    Status: a.status || a.device_status || '',
                    Brand: a.brand || a.vendor || '',
                    Model: a.model || '',
                    Year: a.year || '',
                    Serial: a.serial_number || '',
                    'Service Tag': a.service_tag || '',
                    Location: a.location || '',
                    'Assigned To': a.assigned_to || '',
                    'Warranty Expires': a.warranty_expires || a.warranty_end || '',
                    'Lifecycle Status': a.lifecycle_status || '',
                    Notes: a.notes || '',
                  })),
                  `assets${selectedLocation ? `-${selectedLocation.replace(/\s+/g, '_')}` : ''}.csv`
                )}
                className="flex items-center space-x-1.5 px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
                title="Export assets to CSV"
              >
                <Download className="h-4 w-4" /><span className="hidden sm:inline">Export</span>
              </button>
            </div>

            {/* Table head shared across modes */}
            {(() => {
              // compact=true → fewer columns + tighter padding (used in sidebar mode)
              const tableHead = (hideLocation = false, compact = false) => {
                const th = (label: string) => (
                  <th className={`${compact ? 'px-3 py-2' : 'px-6 py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>{label}</th>
                );
                return (
                  <thead className="bg-gray-50">
                    <tr>
                      {th('Asset')}
                      {th('Type')}
                      {!compact && th('Lifecycle')}
                      {!compact && th('Serial #')}
                      {!hideLocation && th('Location')}
                      {!compact && th('Assigned To')}
                      {th('Warranty')}
                      <th className={`${compact ? 'px-2 py-2' : 'px-3 py-3'} w-8`}></th>
                    </tr>
                  </thead>
                );
              };

              const renderRow = (asset: Asset, hideLocation = false, compact = false) => {
                const assetKey = asset.pk || asset.id || '';
                const status = asset.status || asset.device_status || 'unknown';
                const displayName = asset.name || asset.hostname || 'Unnamed Asset';
                const warrantyDate = asset.warranty_expires || asset.warranty_end;
                const warrantyExpired = warrantyDate ? new Date(warrantyDate) < new Date() : false;
                const warning = getLifecycleWarning(asset);
                const p = compact ? 'px-3 py-2' : 'px-6 py-4';
                return (
                  <React.Fragment key={assetKey}>
                    <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openAssetDetail(asset)}>
                      <td className={`${p} whitespace-nowrap`}>
                        <div className="flex items-center space-x-2">
                          {!compact && <div className={`p-2 rounded-lg flex-shrink-0 ${getAssetTypeColor(asset)}`}>{getAssetIcon(asset)}</div>}
                          <div>
                            <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-900`}>{displayName}</p>
                            <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium mt-0.5 ${getStatusColor(status)}`}>
                              {getStatusIcon(status)}<span className="ml-1">{getStatusLabel(status)}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`${p} whitespace-nowrap`}>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAssetTypeColor(asset)}`}>{getAssetTypeLabel(asset)}</span>
                      </td>
                      {!compact && (
                        <td className={`${p} whitespace-nowrap`}>
                          {warning ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getLifecycleBadgeClasses(warning.severity)}`}>
                              <AlertTriangle className="h-3 w-3 mr-1" />{warning.label}
                            </span>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      )}
                      {!compact && <td className={`${p} whitespace-nowrap text-xs font-mono text-gray-700`}>{asset.serial_number || <span className="text-gray-400">—</span>}</td>}
                      {!hideLocation && <td className={`${p} whitespace-nowrap text-xs text-gray-700`}>{asset.location || <span className="text-gray-400">—</span>}</td>}
                      {!compact && <td className={`${p} whitespace-nowrap text-xs text-gray-700`}>{asset.assigned_to || <span className="text-gray-400">Unassigned</span>}</td>}
                      <td className={`${p} whitespace-nowrap text-xs`}>
                        {warrantyDate ? (
                          <span className={warrantyExpired ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                            {warrantyExpired ? 'Exp. ' : ''}{formatDate(warrantyDate)}
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className={`${compact ? 'px-2 py-2' : 'px-3 py-4'} text-center text-gray-400`}>
                        <ChevronRight className="h-4 w-4" />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              };

              const emptyState = (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
                  <HardDrive className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">No assets found</h3>
                  <p className="text-sm text-gray-500">
                    {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                      ? 'Try adjusting your search or filters'
                      : 'No assets are currently available'}
                  </p>
                </div>
              );

              /* ── FLAT VIEW ── */
              if (locationView === 'flat') {
                return displayedAssets.length > 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        {tableHead(false)}
                        <tbody className="bg-white divide-y divide-gray-200">{displayedAssets.map(a => renderRow(a, false))}</tbody>
                      </table>
                    </div>
                    <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                      Showing {displayedAssets.length} of {assets.length} asset{assets.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                ) : emptyState;
              }

              /* ── GROUPED VIEW (Option B) ── */
              if (locationView === 'grouped') {
                return grouped.length > 0 ? (
                  <div className="space-y-4">
                    {grouped.map(({ location, assets: groupAssets }) => (
                      <div key={location} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        {/* Location header */}
                        <div className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100">
                          <MapPin className="h-4 w-4 text-purple-500 flex-shrink-0" />
                          <span className="text-sm font-semibold text-purple-900">{location}</span>
                          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            {groupAssets.length} asset{groupAssets.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-100">
                            {tableHead(true, false)}
                            <tbody className="bg-white divide-y divide-gray-100">{groupAssets.map(a => renderRow(a, true, false))}</tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                    <div className="text-xs text-gray-500 px-1">
                      {displayedAssets.length} asset{displayedAssets.length !== 1 ? 's' : ''} across {grouped.length} site{grouped.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                ) : emptyState;
              }

              /* ── SIDEBAR VIEW (Option C) ── */
              if (locationView === 'sidebar') {
                const sidebarAssets = selectedLocation
                  ? filteredAssets.filter(a => (a.location || 'Unassigned') === selectedLocation)
                  : filteredAssets;
                return (
                  <div className="flex gap-4 items-start">
                    {/* Left sidebar — location list */}
                    <div className="w-52 flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sites</p>
                      </div>
                      <ul className="divide-y divide-gray-100">
                        <li>
                          <button
                            onClick={() => setSelectedLocation(null)}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${!selectedLocation ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                          >
                            <span>All Sites</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${!selectedLocation ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                              {filteredAssets.length}
                            </span>
                          </button>
                        </li>
                        {allLocations.map(loc => {
                          const count = filteredAssets.filter(a => (a.location || 'Unassigned') === loc).length;
                          if (count === 0) return null;
                          const active = selectedLocation === loc;
                          return (
                            <li key={loc}>
                              <button
                                onClick={() => setSelectedLocation(loc)}
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${active ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                              >
                                <span className="truncate text-left">{loc}</span>
                                <span className={`ml-2 flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {count}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    {/* Right panel — table */}
                    <div className="flex-1 min-w-0">
                      {sidebarAssets.length > 0 ? (
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                          {selectedLocation && (
                            <div className="flex items-center space-x-2 px-6 py-3 bg-purple-50 border-b border-purple-100">
                              <MapPin className="h-4 w-4 text-purple-500" />
                              <span className="text-sm font-semibold text-purple-900">{selectedLocation}</span>
                            </div>
                          )}
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-100">
                              {tableHead(!!selectedLocation, true)}
                              <tbody className="bg-white divide-y divide-gray-100">{sidebarAssets.map(a => renderRow(a, !!selectedLocation, true))}</tbody>
                            </table>
                          </div>
                          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                            {sidebarAssets.length} asset{sidebarAssets.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-10 text-center text-sm text-gray-500">
                          No assets at this location
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return null;
            })()}

          </div>
        );
      })()}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (() => {
        const filteredVendors = vendors.filter((vendor) => {
          if (!searchTerm) return true;
          const term = searchTerm.toLowerCase();
          return [
            vendor.vendor_name, vendor.name, vendor.primary_contact_name,
            vendor.contact_person, vendor.contact_email, vendor.email,
            vendor.vendor_type, vendor.vendor_code, vendor.vendor_account_number
          ].filter(Boolean).join(' ').toLowerCase().includes(term);
        });

        // Build location list from vendors' own network_location_name field
        const vendorLocations = Array.from(
          new Set(filteredVendors.map(v => v.network_location_name || 'Not specified'))
        ).sort();

        const vendorsForLocation = (loc: string | null) => {
          if (!loc) return filteredVendors;
          return filteredVendors.filter(v => (v.network_location_name || 'Not specified') === loc);
        };

        // Flat mode shows all vendors; sidebar mode filters by selected location
        const displayVendors = vendorLocationView === 'flat'
          ? filteredVendors
          : vendorsForLocation(selectedVendorLocation);

        const handleExport = () => {
          exportToCSV(
            displayVendors.map(v => ({
              Name: v.vendor_name || v.name || '',
              Type: v.vendor_type || '',
              Status: v.vendor_status || '',
              Rating: v.vendor_rating ?? '',
              'Account Number': v.vendor_account_number || '',
              Contact: v.primary_contact_name || v.contact_person || '',
              Email: v.contact_email || v.email || '',
              Phone: v.contact_number || v.phone || '',
              Website: v.contact_website || v.website || '',
              Address: v.business_address || '',
              Location: selectedVendorLocation || 'All',
            })),
            `vendors${selectedVendorLocation ? `-${selectedVendorLocation.replace(/\s+/g, '_')}` : ''}.csv`
          );
        };

        const vendorTable = (rows: Vendor[], compact = false) => {
          const p = compact ? 'px-3 py-2' : 'px-6 py-3';
          const pd = compact ? 'px-3 py-2' : 'px-6 py-4';
          const divClass = compact ? 'divide-gray-100' : 'divide-gray-200';
          const getVendorStatus = (v: Vendor) => v.vendor_status || 'active';
          const getVendorStatusColor = (s: string) => {
            switch (s) {
              case 'preferred': return 'bg-green-100 text-green-700';
              case 'active': return 'bg-blue-100 text-blue-700';
              case 'inactive': return 'bg-gray-100 text-gray-500';
              case 'discontinued': return 'bg-red-100 text-red-700';
              default: return 'bg-gray-100 text-gray-500';
            }
          };
          return (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className={`min-w-full divide-y ${divClass}`}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`${p} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Vendor</th>
                    {!compact && <th className={`${p} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Type / Status</th>}
                    {!compact && <th className={`${p} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Contact</th>}
                    <th className={`${p} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Email</th>
                    <th className={`${p} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Phone</th>
                    <th className={`${p} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Website</th>
                    <th className={`${compact ? 'px-3 py-2' : 'px-3 py-3'} w-16`}></th>
                  </tr>
                </thead>
                <tbody className={`bg-white divide-y ${divClass}`}>
                  {rows.map((vendor) => {
                    const displayName = vendor.vendor_name || vendor.name || 'Unknown Vendor';
                    const contactEmail = vendor.contact_email || vendor.email || '';
                    const contactPhone = vendor.contact_number || vendor.phone || '';
                    const contactWebsite = vendor.contact_website || vendor.website || '';
                    const contactPerson = vendor.primary_contact_name || vendor.contact_person || '';
                    const vendorType = vendor.vendor_type;
                    const vendorStatus = getVendorStatus(vendor);
                    return (
                    <tr key={vendor.id || vendor.pk} className="hover:bg-gray-50 transition-colors">
                      <td className={`${pd} whitespace-nowrap`}>
                        <div className="flex items-center space-x-2">
                          {!compact && <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0"><Building2 className="h-4 w-4 text-orange-600" /></div>}
                          <div>
                            <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-900`}>{displayName}</p>
                            {!compact && vendor.description && (
                              <p className="text-xs text-gray-400 max-w-xs truncate">{vendor.description}</p>
                            )}
                            {!compact && !vendor.description && vendor.vendor_account_number && (
                              <p className="text-xs text-gray-400">Acct # {vendor.vendor_account_number}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      {!compact && (
                        <td className={`${pd} whitespace-nowrap`}>
                          <div className="space-y-1">
                            {vendorType && <span className="block text-xs text-gray-600 capitalize">{vendorType}</span>}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getVendorStatusColor(vendorStatus)} capitalize`}>{vendorStatus}</span>
                          </div>
                        </td>
                      )}
                      {!compact && <td className={`${pd} whitespace-nowrap text-sm text-gray-700`}>{contactPerson || <span className="text-gray-400">—</span>}</td>}
                      <td className={`${pd} whitespace-nowrap text-xs`}>
                        {contactEmail ? <a href={`mailto:${contactEmail}`} className="text-green-600 hover:text-green-700 hover:underline">{contactEmail}</a> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className={`${pd} whitespace-nowrap text-xs`}>
                        {contactPhone ? <a href={`tel:${contactPhone}`} className="text-purple-600 hover:text-purple-700 hover:underline">{contactPhone}</a> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className={`${pd} whitespace-nowrap text-xs`}>
                        {contactWebsite ? <a href={contactWebsite} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 hover:underline">Visit →</a> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className={`${compact ? 'px-3 py-2' : 'px-3 py-4'} whitespace-nowrap text-right`}>
                        <button
                          onClick={() => setSelectedVendorDetail(vendor)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <Eye className="h-3 w-3 mr-1" />View
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              {rows.length} vendor{rows.length !== 1 ? 's' : ''}
            </div>
          </div>
          );
        };

        const emptyVendors = (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-10 text-center">
            <Building2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">{searchTerm ? 'No vendors match your search' : 'No vendors at this location'}</p>
          </div>
        );

        return (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by vendor name, contact, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
              </div>
              {/* View toggle */}
              <div className="flex items-center rounded-xl border border-gray-300 overflow-hidden bg-white text-sm flex-shrink-0">
                <button
                  onClick={() => setVendorLocationView('flat')}
                  className={`px-3 py-2.5 flex items-center space-x-1.5 transition-colors ${vendorLocationView === 'flat' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <Building2 className="h-4 w-4" /><span>All</span>
                </button>
                <button
                  onClick={() => { setVendorLocationView('sidebar'); if (!selectedVendorLocation && vendorLocations.length > 0) setSelectedVendorLocation(vendorLocations[0]); }}
                  className={`px-3 py-2.5 flex items-center space-x-1.5 border-l border-gray-300 transition-colors ${vendorLocationView === 'sidebar' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <MapPin className="h-4 w-4" /><span>By Site</span>
                </button>
              </div>
              {/* Export */}
              <button
                onClick={handleExport}
                className="flex items-center space-x-1.5 px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
                title="Export vendors to CSV"
              >
                <Download className="h-4 w-4" /><span>Export</span>
              </button>
            </div>

            {/* Flat view */}
            {vendorLocationView === 'flat' && (
              displayVendors.length > 0 ? vendorTable(displayVendors) : emptyVendors
            )}

            {/* Sidebar view */}
            {vendorLocationView === 'sidebar' && (
              <div className="flex gap-4 items-start">
                <div className="w-52 flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sites</p>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    <li>
                      <button
                        onClick={() => setSelectedVendorLocation(null)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${!selectedVendorLocation ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        <span>All Sites</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${!selectedVendorLocation ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{filteredVendors.length}</span>
                      </button>
                    </li>
                    {vendorLocations.map(loc => {
                      const count = vendorsForLocation(loc).length;
                      const active = selectedVendorLocation === loc;
                      return (
                        <li key={loc}>
                          <button
                            onClick={() => setSelectedVendorLocation(loc)}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${active ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                          >
                            <span className="truncate text-left">{loc}</span>
                            <span className={`ml-2 flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="flex-1 min-w-0">
                  {selectedVendorLocation && (() => {
                    const locAddress = displayVendors.find(v => v.network_location_address)?.network_location_address;
                    return (
                      <div className="mb-3">
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-purple-500 flex-shrink-0" />
                          <span className="text-sm font-semibold text-purple-900">{selectedVendorLocation}</span>
                        </div>
                        {locAddress && (
                          <p className="mt-0.5 ml-6 text-xs text-gray-500">{locAddress}</p>
                        )}
                      </div>
                    );
                  })()}
                  {displayVendors.length > 0 ? vendorTable(displayVendors, true) : emptyVendors}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Credentials Tab */}
      {activeTab === 'credentials' && (() => {
        const filteredCredentials = credentials.filter((cred) => {
          if (!searchTerm) return true;
          const term = searchTerm.toLowerCase();
          return [cred.service_name, cred.username, cred.category]
            .filter(Boolean).join(' ').toLowerCase().includes(term);
        });

        return (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by service name, username, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-purple-500 focus:border-purple-500 text-sm"
            />
          </div>

          {/* Table */}
          {filteredCredentials.length > 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Password</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCredentials.map((credential) => {
                      const isExpired = credential.expires ? new Date(credential.expires) < new Date() : false;
                      return (
                        <tr key={credential.id} className="hover:bg-gray-50 transition-colors">
                          {/* Service */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                                <Key className="h-4 w-4 text-purple-600" />
                              </div>
                              <p className="text-sm font-semibold text-gray-900">{credential.service_name}</p>
                            </div>
                          </td>
                          {/* Category */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getCategoryBadge(credential.category)}`}>
                              {credential.category}
                            </span>
                          </td>
                          {/* Username */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderUsernameCell(credential)}
                          </td>
                          {/* Password */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderSecretCell(credential)}
                          </td>
                          {/* URL */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {credential.website
                              ? <a href={credential.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 hover:underline">Visit →</a>
                              : <span className="text-gray-400">—</span>}
                          </td>
                          {/* Last Updated */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {credential.last_updated ? formatDate(credential.last_updated) : <span className="text-gray-400">—</span>}
                          </td>
                          {/* Expires */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {credential.expires ? (
                              <span className={isExpired ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                                {isExpired ? 'Expired ' : ''}{formatDate(credential.expires)}
                              </span>
                            ) : (
                              <span className="text-gray-400">Never</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                {filteredCredentials.length} credential{filteredCredentials.length !== 1 ? 's' : ''}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
              <Key className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No credentials found</h3>
              <p className="text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search' : 'No credentials are currently stored'}
              </p>
            </div>
          )}
        </div>
        );
      })()}

      {/* Add Credential Modal */}
      {showAddCredentialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <Key className="h-5 w-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Add New Credential</h3>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6">
              <div className="space-y-4">
                {/* Service Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newCredential.title}
                    onChange={(e) => setNewCredential(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Router Admin, Office 365, Main Server"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">What is this credential for?</p>
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={newCredential.username}
                    onChange={(e) => setNewCredential(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="admin, user@domain.com, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 font-mono"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newCredential.password}
                    onChange={(e) => setNewCredential(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Password, API key, or access token"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 font-mono"
                  />
                </div>

                {/* URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL
                  </label>
                  <input
                    type="url"
                    value={newCredential.url}
                    onChange={(e) => setNewCredential(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://admin.router.com, 192.168.1.1, server.domain.com:3389"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Where to use this credential</p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newCredential.notes}
                    onChange={(e) => setNewCredential(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional information: 2FA backup codes, special instructions, port numbers, access levels, etc."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Store any additional details like 2FA info, special ports, access levels, or instructions</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCredential}
                  disabled={!newCredential.title || isSubmitting}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span>Add Credential</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Support Tools Tab */}
      {activeTab === 'tools' && (
        <SupportToolsTab
          customerId={customerId}
          customerName={customerName}
        />
      )}

      {/* Vendor Detail Drawer */}
      {/* Asset Detail Modal — Details + related Credentials */}
      {selectedAssetDetail && (() => {
        const a = selectedAssetDetail;
        const displayName = a.name || a.hostname || 'Unnamed Asset';
        const warrantyDate = a.warranty_expires || a.warranty_end;
        const warrantyExpired = warrantyDate ? new Date(warrantyDate) < new Date() : false;
        const warning = getLifecycleWarning(a);
        const closeModal = () => setSelectedAssetDetail(null);
        const detailFields = [
          { label: 'Brand', value: a.brand || a.vendor },
          { label: 'Model', value: a.model },
          { label: 'Year', value: a.year?.toString() },
          { label: 'Variant / Spec', value: a.variant },
          { label: 'Hostname', value: a.hostname, mono: true },
          { label: 'Serial Number', value: a.serial_number, mono: true },
          a.service_tag ? { label: 'Service Tag', value: a.service_tag, mono: true } : null,
          { label: 'Location', value: a.location },
          { label: 'Assigned To', value: a.assigned_to },
        ].filter(Boolean) as { label: string; value?: string; mono?: boolean }[];

        return (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-40 z-40" onClick={closeModal} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col pointer-events-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${getAssetTypeColor(a)}`}>{getAssetIcon(a)}</div>
                    <div className="min-w-0">
                      <h2 className="text-lg font-bold text-gray-900 truncate">{displayName}</h2>
                      <div className="flex items-center space-x-2 mt-0.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(a.status || a.device_status || 'unknown')}`}>
                          {getStatusIcon(a.status || a.device_status || 'unknown')}
                          <span className="ml-1">{getStatusLabel(a.status || a.device_status || 'unknown')}</span>
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAssetTypeColor(a)}`}>{getAssetTypeLabel(a)}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeModal}
                    className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex space-x-6 px-6 border-b border-gray-200 flex-shrink-0">
                  {([
                    { key: 'details' as const, label: 'Details', icon: Computer },
                    { key: 'credentials' as const, label: 'Credentials', icon: Key },
                  ]).map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setAssetModalTab(tab.key)}
                      className={`flex items-center space-x-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                        assetModalTab === tab.key
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <tab.icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                      {tab.key === 'credentials' && !assetCredentialsLoading && assetCredentials.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                          {assetCredentials.length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                  {assetModalTab === 'details' ? (
                    <div className="space-y-4">
                      {warning && warning.severity !== 'neutral' && (
                        <div className={`rounded-lg p-3 border ${getLifecycleBannerClasses(warning.severity)}`}>
                          <div className="flex items-start space-x-2">
                            <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${getLifecycleIconClasses(warning.severity)}`} />
                            <div>
                              <p className="text-sm font-medium">{warning.message}</p>
                              {warning.notes && <p className="text-xs mt-1 opacity-80">{warning.notes}</p>}
                              <div className="flex gap-4 mt-1">
                                {warning.eos_date && <span className="text-xs">End of Support: <strong>{formatDate(warning.eos_date)}</strong></span>}
                                {warning.eol_date && <span className="text-xs">End of Life: <strong>{formatDate(warning.eol_date)}</strong></span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {detailFields.map(f => (
                          <div key={f.label} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <p className="text-xs text-gray-500 mb-1">{f.label}</p>
                            <p className={`text-sm font-semibold text-gray-900 ${f.mono ? 'font-mono' : ''}`}>{f.value || '—'}</p>
                          </div>
                        ))}
                        {(a.purchase_date || a.warranty_start) && (
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                            <p className="text-xs text-blue-600 mb-1">Purchase / Warranty Start</p>
                            <p className="text-sm font-semibold text-gray-900">{formatDate(a.purchase_date || a.warranty_start || '')}</p>
                          </div>
                        )}
                        {warrantyDate && (
                          <div className={`rounded-lg p-3 border ${warrantyExpired ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'}`}>
                            <p className={`text-xs mb-1 ${warrantyExpired ? 'text-red-600' : 'text-amber-600'}`}>Warranty Expiration</p>
                            <p className="text-sm font-semibold text-gray-900">{formatDate(warrantyDate)}</p>
                          </div>
                        )}
                      </div>
                      {a.notes && (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1 font-semibold uppercase tracking-wider">Notes</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{a.notes}</p>
                        </div>
                      )}
                    </div>
                  ) : assetCredentialsLoading ? (
                    <div className="flex items-center justify-center py-12 text-gray-400">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span className="text-sm">Loading credentials…</span>
                    </div>
                  ) : assetCredentials.length === 0 ? (
                    <div className="text-center py-12">
                      <Key className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No credentials linked to this asset.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {assetCredentials.map(credential => (
                        <div key={credential.id} className="border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                              <Key className="h-4 w-4 text-purple-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{credential.service_name}</p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getCategoryBadge(credential.category)}`}>
                                {credential.category}
                              </span>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Username</p>
                              {renderUsernameCell(credential)}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Password</p>
                              {renderSecretCell(credential)}
                            </div>
                          </div>
                          {credential.website && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <a
                                href={credential.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
                              >
                                {credential.website} →
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {selectedVendorDetail && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={() => setSelectedVendorDetail(null)}
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50 flex-shrink-0">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                  <Building2 className="h-5 w-5 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 truncate">{selectedVendorDetail.name}</h2>
                  <p className="text-xs text-gray-500">Vendor Details</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedVendorDetail(null)}
                className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Vendor Info */}
              {(() => {
                const v = selectedVendorDetail;
                const hasInfo = v.vendor_type || v.vendor_rating || v.vendor_code || v.vendor_account_number || (v.vendor_status && v.vendor_status !== 'active');
                if (!hasInfo) return null;
                const statusColor = (s: string) => {
                  switch (s) {
                    case 'preferred': return 'bg-green-100 text-green-700';
                    case 'active': return 'bg-blue-100 text-blue-700';
                    case 'inactive': return 'bg-gray-100 text-gray-500';
                    case 'discontinued': return 'bg-red-100 text-red-700';
                    default: return 'bg-gray-100 text-gray-500';
                  }
                };
                return (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Vendor Details</h3>
                    <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-3">
                      {v.vendor_type && (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Type</p>
                          <p className="text-sm font-medium text-gray-900 capitalize">{v.vendor_type}</p>
                        </div>
                      )}
                      {v.vendor_status && (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Status</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(v.vendor_status)} capitalize`}>{v.vendor_status}</span>
                        </div>
                      )}
                      {v.vendor_rating ? (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Rating</p>
                          <p className="text-sm font-medium text-gray-900">{'★'.repeat(v.vendor_rating)}{'☆'.repeat(5 - v.vendor_rating)}</p>
                        </div>
                      ) : null}
                      {v.vendor_code && (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Vendor Code</p>
                          <p className="text-sm font-mono text-gray-900">{v.vendor_code}</p>
                        </div>
                      )}
                      {v.vendor_account_number && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500 mb-0.5">Account Number</p>
                          <p className="text-sm font-mono text-gray-900">{v.vendor_account_number}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Contact Info */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact Information</h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                  {(() => {
                    const v = selectedVendorDetail;
                    const contact = v.primary_contact_name || v.contact_person;
                    const acctRep = v.account_name;
                    const email = v.contact_email || v.email;
                    const phone = v.contact_number || v.phone;
                    const website = v.contact_website || v.website;
                    const hasAny = contact || acctRep || email || phone || website;
                    if (!hasAny) return <p className="text-sm text-gray-400 text-center py-2">No contact information on file</p>;
                    return (
                      <>
                        {contact && (
                          <div className="flex items-start space-x-3">
                            <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Primary Contact</p>
                              <p className="text-sm font-medium text-gray-900">{contact}</p>
                            </div>
                          </div>
                        )}
                        {acctRep && acctRep !== contact && (
                          <div className="flex items-start space-x-3">
                            <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Account / Reference</p>
                              <p className="text-sm font-medium text-gray-900">{acctRep}</p>
                            </div>
                          </div>
                        )}
                        {email && (
                          <div className="flex items-start space-x-3">
                            <Mail className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Email</p>
                              <a href={`mailto:${email}`} className="text-sm font-medium text-green-600 hover:underline break-all">{email}</a>
                            </div>
                          </div>
                        )}
                        {phone && (
                          <div className="flex items-start space-x-3">
                            <Phone className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                              <a href={`tel:${phone}`} className="text-sm font-medium text-purple-600 hover:underline">{phone}</a>
                            </div>
                          </div>
                        )}
                        {website && (
                          <div className="flex items-start space-x-3">
                            <Globe className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Website</p>
                              <a href={website} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-indigo-600 hover:underline break-all">{website}</a>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Business / Notes */}
              {(selectedVendorDetail.business_address || selectedVendorDetail.description) && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Business Information</h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    {selectedVendorDetail.business_address && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Address</p>
                        <p className="text-sm text-gray-900 whitespace-pre-line">{selectedVendorDetail.business_address}</p>
                      </div>
                    )}
                    {selectedVendorDetail.description && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Notes</p>
                        <p className="text-sm text-gray-900 whitespace-pre-line">{selectedVendorDetail.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Related Assets */}
              {(() => {
                const v = selectedVendorDetail;
                const vendorDisplayName = v.vendor_name || v.name || '';
                const relatedAssets = assets.filter(a => a.vendor === vendorDisplayName);
                const assetCount = v.assets_count ?? relatedAssets.length;
                return (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Related Assets
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
                        {assetCount}
                      </span>
                    </h3>
                    {relatedAssets.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-xl">No assets linked to this vendor</p>
                    ) : (
                      <div className="space-y-2">
                        {relatedAssets.map(asset => {
                          const status = asset.status || asset.device_status || 'unknown';
                          const displayName = asset.name || asset.hostname || 'Unnamed';
                          const model = [asset.brand, asset.model].filter(Boolean).join(' ');
                          return (
                            <div key={asset.pk || asset.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                              <div className="flex items-center space-x-2 min-w-0">
                                <div className={`p-1.5 rounded-lg flex-shrink-0 ${getAssetTypeColor(asset)}`}>
                                  {getAssetIcon(asset)}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                                  {(model || asset.location) && (
                                    <p className="text-xs text-gray-500 truncate">{[model, asset.location].filter(Boolean).join(' · ')}</p>
                                  )}
                                </div>
                              </div>
                              <span className={`ml-2 flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                {getStatusLabel(status)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ITAssets;
